import { getInitialState } from './data'
import type { AppState, Fund } from './types'

const STORAGE_KEY = 'fund-intelligence-state-v1'

export function loadState(): AppState {
  if (typeof window === 'undefined') return getInitialState()
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY)
    if (!raw) return getInitialState()
    const parsed = JSON.parse(raw) as Partial<AppState>
    const fallback = getInitialState()
    return {
      ...fallback,
      ...parsed,
      funds: Array.isArray(parsed.funds) && parsed.funds.length ? parsed.funds as Fund[] : fallback.funds,
      settings: { ...fallback.settings, ...(parsed.settings ?? {}) },
      portfolio: Array.isArray(parsed.portfolio) ? parsed.portfolio : fallback.portfolio,
      scenarios: Array.isArray(parsed.scenarios) ? parsed.scenarios : fallback.scenarios,
      compareFundIds: Array.isArray(parsed.compareFundIds) ? parsed.compareFundIds : fallback.compareFundIds,
    }
  } catch {
    return getInitialState()
  }
}

export function persistState(state: AppState): void {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(state))
  } catch {
    // Local-first persistence is best-effort; the in-memory state remains usable.
  }
}

function download(filename: string, content: string, type: string): void {
  const blob = new Blob([content], { type })
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = filename
  link.click()
  URL.revokeObjectURL(url)
}

export function exportJSON(state: AppState): void {
  download(`fund-intelligence-${new Date().toISOString().slice(0, 10)}.json`, JSON.stringify(state, null, 2), 'application/json')
}

function csvEscape(value: unknown): string {
  const text = value === null || value === undefined ? '' : String(value)
  return /[",\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text
}

export function exportCSV(funds: Fund[]): void {
  const headers = ['code', 'name', 'category', 'manager', 'price', 'fund_size', 'investors', '1m_return', '3m_return', '1y_return', 'volatility_1y', 'max_drawdown', 'sharpe', 'sortino', 'alpha', 'monthly_flow', 'last_updated']
  const rows = funds.map((fund) => [fund.code, fund.name, fund.category, fund.manager, fund.latestPrice, fund.fundSize, fund.investors, fund.returns['1m'].fund, fund.returns['3m'].fund, fund.returns['1y'].fund, fund.volatility.y1, fund.drawdown.max, fund.sharpe.fund, fund.sortino.fund, fund.alpha.value, fund.cashFlow.monthly, fund.lastUpdated])
  const csv = [headers, ...rows].map((row) => row.map(csvEscape).join(',')).join('\n')
  download(`fund-intelligence-funds-${new Date().toISOString().slice(0, 10)}.csv`, csv, 'text/csv;charset=utf-8')
}

export function resetState(): AppState {
  const next = getInitialState()
  try { window.localStorage.removeItem(STORAGE_KEY) } catch { /* ignore */ }
  return next
}
